import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * Card container with optional header and footer.
 *
 * @param {Object} props
 * @param {boolean} [props.raised=false] - Apply elevated shadow
 * @param {boolean} [props.hoverable=false] - Add hover effect
 * @param {boolean} [props.noPadding=false] - Remove default padding
 * @param {string} [props.className] - Additional classes
 */
export const Card = forwardRef(function Card(
  {
    raised = false,
    hoverable = false,
    noPadding = false,
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        'bg-white rounded-lg border border-neutral-200',
        raised ? 'shadow-md' : 'shadow-sm',
        hoverable && 'hover:shadow-md hover:border-neutral-300 transition-shadow duration-150',
        !noPadding && 'p-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

/**
 * Card header section.
 */
export function CardHeader({ className, children, ...props }) {
  return (
    <div
      className={cn('pb-4 border-b border-neutral-200', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card body section.
 */
export function CardBody({ className, children, ...props }) {
  return (
    <div className={cn('py-4', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Card footer section.
 */
export function CardFooter({ className, children, ...props }) {
  return (
    <div
      className={cn('pt-4 border-t border-neutral-200', className)}
      {...props}
    >
      {children}
    </div>
  );
}
